import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, ArrowRight, Mail } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { apiClient } from "@/lib/api";

const NEXT_STEPS = [
  {
    n: "01",
    title: "Advisor review",
    body: "Within one business day, your advisor reviews your snapshot.",
  },
  {
    n: "02",
    title: "Personalized consultation",
    body: "A no-pressure call at your preferred time — by phone or email.",
  },
  {
    n: "03",
    title: "Custom recommendations",
    body: "Products and strategies built around your goals, not a quota.",
  },
];

export default function ApplySuccess() {
  const [params] = useSearchParams();
  const ref = params.get("ref") || "";
  const [advisor, setAdvisor] = useState(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    if (!ref) return;
    apiClient
      .get(`/leads/${ref}`)
      .then((r) => setAdvisor(r.data?.assigned_advisor || null))
      .catch(() => {});
  }, [ref]);

  return (
    <div data-testid="apply-success-page" className="min-h-screen bg-lw-ivory">
      <Nav />
      <main className="pt-28 sm:pt-32 lg:pt-36 pb-20">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="flex items-center gap-3 mb-5">
            <span className="flex items-center justify-center w-11 h-11 rounded-full bg-lw-gold/15 text-lw-gold">
              <CheckCircle2 size={22} />
            </span>
            <span className="lw-eyebrow">Application received</span>
          </div>
          <h1
            data-testid="apply-success-headline"
            className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-lw-navy leading-[1.1]"
          >
            Thank you. <span className="italic">Your advisor will be in touch.</span>
          </h1>
          <p className="mt-4 text-base sm:text-lg text-lw-muted leading-relaxed max-w-2xl">
            We've received your information and will reach out within one
            business day. Keep your reference number handy if you need to get
            in touch in the meantime.
          </p>

          {ref && (
            <div
              data-testid="apply-success-reference"
              className="mt-7 inline-flex items-center gap-3 px-5 py-3 rounded-full border border-dashed border-lw-gold bg-white"
            >
              <Mail size={16} className="text-lw-gold" />
              <span className="font-sans-ui text-xs uppercase tracking-[0.18em] text-lw-muted">
                Reference
              </span>
              <span className="font-mono text-sm text-lw-navy break-all">
                {ref}
              </span>
            </div>
          )}

          {advisor && (
            <div
              data-testid="apply-success-advisor"
              className="mt-8 rounded-2xl border border-lw-navy/10 bg-white p-6 sm:p-7"
            >
              <span className="lw-eyebrow">Your dedicated advisor</span>
              <p className="mt-2 font-serif-display text-2xl text-lw-navy">
                {advisor}
              </p>
            </div>
          )}

          <div className="mt-10">
            <span className="lw-eyebrow">What happens next</span>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
              {NEXT_STEPS.map((s) => (
                <div
                  key={s.n}
                  data-testid={`apply-success-step-${s.n}`}
                  className="rounded-xl border border-lw-navy/10 bg-white p-5"
                >
                  <span className="font-sans-ui text-xs tracking-[0.2em] uppercase text-lw-gold">
                    {s.n}
                  </span>
                  <h3 className="mt-2 font-serif-display text-lg text-lw-navy">
                    {s.title}
                  </h3>
                  <p className="mt-1 text-sm text-lw-muted leading-relaxed">
                    {s.body}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/" data-testid="apply-success-home" className="btn-gold">
              Return home <ArrowRight size={18} />
            </Link>
            <Link to="/tools" data-testid="apply-success-tools" className="btn-ghost-navy">
              Explore our tools
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
